import React, { useState, useEffect, useRef } from 'react';
import {
  X, Mail, Send, Loader2, Check,
  AlertCircle, User, ChevronDown, FileText, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Available tokens that can be inserted into emails
const AVAILABLE_TOKENS = [
  { token: '{customer_name}', label: 'First Name', description: 'Customer first name' },
  { token: '{full_name}', label: 'Full Name', description: 'Customer full name' },
  { token: '{service}', label: 'Service', description: 'Service type' },
  { token: '{amount}', label: 'Amount', description: 'Job/invoice amount' },
  { token: '{balance}', label: 'Balance', description: 'Outstanding balance' },
  { token: '{date}', label: 'Date', description: 'Scheduled date' },
  { token: '{address}', label: 'Address', description: 'Customer address' },
  { token: '{business_name}', label: 'Business', description: 'Your company name' },
  { token: '{payment_link}', label: 'Pay Link', description: 'Online payment link' },
];

// Default email templates
const DEFAULT_TEMPLATES = [
  {
    id: 'reminder',
    name: 'Appointment Reminder',
    subject: 'Reminder: Your appointment tomorrow',
    body: 'Hi {customer_name},\n\nJust a friendly reminder that your {service} is scheduled for tomorrow.\n\nIf you have any questions, please don\'t hesitate to get in touch.\n\nBest regards,\n{business_name}'
  },
  {
    id: 'thank_you',
    name: 'Thank You',
    subject: 'Thank you for your business',
    body: 'Hi {customer_name},\n\nThank you for choosing {business_name}. We appreciate your business and look forward to serving you again.\n\nBest regards,\n{business_name}'
  },
  {
    id: 'payment_reminder',
    name: 'Payment Reminder',
    subject: 'Payment Reminder - £{balance} outstanding',
    body: 'Hi {customer_name},\n\nThis is a friendly reminder that you have an outstanding balance of £{balance}.\n\nYou can pay securely online using this link: {payment_link}\n\nIf you have any questions, please get in touch.\n\nBest regards,\n{business_name}'
  },
  {
    id: 'reschedule',
    name: 'Appointment Rescheduled',
    subject: 'Your appointment has been rescheduled',
    body: 'Hi {customer_name},\n\nYour {service} has been rescheduled to {date}.\n\nIf this time doesn\'t work for you, please let us know and we\'ll find an alternative.\n\nBest regards,\n{business_name}'
  },
  {
    id: 'custom',
    name: 'Custom Email',
    subject: '',
    body: ''
  }
];

const EmailComposeModal = ({ 
  isOpen, 
  onClose, 
  recipient = null, // { id, name, email, customer_id }
  context = {}, // { service, amount, date, balance, etc. }
  onEmailSent = null
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [activeField, setActiveField] = useState('body'); // 'subject' or 'body'
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  // Load template when selected
  useEffect(() => {
    const template = DEFAULT_TEMPLATES.find(t => t.id === selectedTemplate);
    if (template && template.id !== 'custom') {
      setSubject(replaceTokens(template.subject));
      setBody(replaceTokens(template.body));
    }
  }, [selectedTemplate]);

  // Replace tokens with actual values
  const replaceTokens = (text) => {
    if (!text) return text;
    
    const customerName = recipient?.name?.split(' ')[0] || 'Customer';
    const fullName = recipient?.name || 'Customer';
    
    return text
      .replace(/{customer_name}/g, customerName)
      .replace(/{full_name}/g, fullName)
      .replace(/{service}/g, context.service || 'service')
      .replace(/{amount}/g, context.amount?.toFixed(2) || '0.00')
      .replace(/{balance}/g, context.balance?.toFixed(2) || '0.00')
      .replace(/{date}/g, context.date || '')
      .replace(/{address}/g, recipient?.address || context.address || '')
      .replace(/{business_name}/g, context.business_name || 'Our Company')
      .replace(/{payment_link}/g, context.payment_link || '[Payment Link]');
  };

  // Insert token at cursor position
  const insertToken = (token) => {
    const ref = activeField === 'subject' ? subjectRef : bodyRef;
    const setter = activeField === 'subject' ? setSubject : setBody;
    const value = activeField === 'subject' ? subject : body;
    
    if (!ref.current) return;
    
    const start = ref.current.selectionStart;
    const end = ref.current.selectionEnd;
    const newValue = value.substring(0, start) + token + value.substring(end);
    setter(newValue);
    
    // Set cursor position after token
    setTimeout(() => {
      ref.current.selectionStart = ref.current.selectionEnd = start + token.length;
      ref.current.focus();
    }, 0);
    
    setShowTokens(false);
  };

  const handleSend = async () => {
    if (!recipient?.email) {
      toast.error('No email address available');
      return;
    }
    
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (!body.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      const response = await fetch(`${API}/api/customers/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: recipient.customer_id || recipient.id,
          recipient_email: recipient.email,
          subject: subject,
          body: body
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }
      
      toast.success('Email sent successfully');
      
      if (onEmailSent) {
        onEmailSent();
      }
      
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl modal-shell-h overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Mail size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Compose Email</h2>
              <p className="text-sm text-blue-100">
                To: {recipient?.email || 'No email'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Recipient Card */}
          <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <User size={18} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-zinc-900">{recipient?.name || 'Customer'}</p>
              <p className="text-sm text-zinc-500">{recipient?.email || 'No email'}</p>
            </div>
            {!recipient?.email && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
                <AlertCircle size={12} />
                No email
              </span>
            )}
          </div>

          {/* Template Selector */}
          <div className="relative">
            <button
              onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
              className="w-full flex items-center justify-between p-3 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-zinc-500" />
                <span className="text-sm text-zinc-700">
                  {DEFAULT_TEMPLATES.find(t => t.id === selectedTemplate)?.name || 'Select Template'}
                </span>
              </div>
              <ChevronDown size={16} className={`text-zinc-400 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showTemplateDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border z-10 max-h-60 overflow-y-auto">
                {DEFAULT_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplate(template.id);
                      setShowTemplateDropdown(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-zinc-50 text-left"
                  >
                    <span className={`w-2 h-2 rounded-full ${selectedTemplate === template.id ? 'bg-blue-500' : 'bg-zinc-200'}`} />
                    <span className="text-sm text-zinc-700">{template.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subject Input */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Subject</label>
            <input
              ref={subjectRef}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setActiveField('subject')}
              placeholder="Enter email subject..."
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Message Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-zinc-700">Message</label>
              <button
                onClick={() => setShowTokens(!showTokens)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
              >
                <Zap size={12} />
                Insert Variable
              </button>
            </div>
            
            {showTokens && (
              <div className="mb-2 p-2 bg-blue-50 rounded-lg flex flex-wrap gap-1">
                {AVAILABLE_TOKENS.map(t => (
                  <button
                    key={t.token}
                    onClick={() => insertToken(t.token)}
                    className="px-2 py-1 bg-white text-xs text-blue-600 rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                    title={t.description}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => setActiveField('body')}
              placeholder="Type your message here..."
              rows={8}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-zinc-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-zinc-600 hover:text-zinc-800 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !recipient?.email}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 text-white rounded-xl font-medium transition-colors"
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposeModal;
